import React, { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { ClipboardField } from 'components/ClipboardField'
import { Spinner } from 'components/Spinner'

import { GET_STATUS } from 'root-redux/actions/user'
import { selectActionList } from 'root-redux/selects/common'
import {
  selectIsUserHasAccount,
  selectIsUserInAppPurchased,
  selectIsUserUpsellPurchased,
  selectUserLoginEmail,
} from 'root-redux/selects/user'

import { useIsEnhancedPostPaymentFlow } from 'hooks/useIsEnhancedPostPaymentFlow'
import { usePostPaymentOrder } from 'hooks/usePostPaymentOrder'
import { useUserRedirectionLink } from 'hooks/useUserRedirectionLink'

import { getAppConfigMap } from 'helpers/getAppConfigMap'

import { eventLogger } from 'services/eventLogger.service'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import paymentSuccessIcon from 'assets/images/sprite/green-successfull-tick.svg'

import { StyledDownloadLong as S } from './DownloadLong.styles'
import { IMAGES_BY_APP, SUBTITLE_KEY_BY_APP } from './constants'

export const DownloadLong: React.FC = () => {
  const { t } = useTranslation()

  const { appName, appConfig } = getAppConfigMap()
  const { supportMail, supportLink } = appConfig

  const redirectionLink = useUserRedirectionLink()
  const hasProgressBar = useIsEnhancedPostPaymentFlow()
  const { isAccountBeforeUpsellOrder } = usePostPaymentOrder()

  const userEmail = useSelector(selectUserLoginEmail)
  const isUserHasAccount = useSelector(selectIsUserHasAccount)
  const isInAppUpsellPurchased = useSelector(selectIsUserInAppPurchased)
  const isUpsellPurchased = useSelector(selectIsUserUpsellPurchased)
  const fetchingActionsList = useSelector(selectActionList)

  const isLoadingUserStatus = fetchingActionsList.includes(GET_STATUS)

  const saleImageSrc = IMAGES_BY_APP[appName]

  const hasPurchasedUpsell = isInAppUpsellPurchased || isUpsellPurchased

  useEffect(() => {
    eventLogger.logGettingAppShown()
  }, [])

  const handleContinue = () => {
    window.location.href = redirectionLink
  }

  const handleClick = () => {
    googleAnalyticsLogger.logDownloadButtonPressed()
    eventLogger.logDownloadTap()
    handleContinue()
  }

  const renderContent = () => {
    if (isUserHasAccount) {
      return (
        <S.Container>
          {isAccountBeforeUpsellOrder ? (
            <S.Title $withoutMargin>{t('downloadApp.subtitle')}</S.Title>
          ) : (
            <>
              <S.SvgImage svg={paymentSuccessIcon} width={48} />
              <S.Title>{t('downloadApp.title')}</S.Title>
              <S.PostDownloadTitle>
                {t('downloadApp.subtitle')}
              </S.PostDownloadTitle>
            </>
          )}

          <S.PostDownloadSteps>
            <li>
              <Trans i18nKey="downloadApp.firstStep" context={appName} />
            </li>
            <li>
              <Trans i18nKey="downloadApp.enterEmail" />
            </li>
            <ClipboardField
              tooltipText={t('downloadApp.emailCopied')}
              value={userEmail}
            />
            <li>
              <Trans i18nKey="downloadApp.enjoyYourJourney" />
            </li>
          </S.PostDownloadSteps>

          <S.Button type="button" onClick={handleClick}>
            {t('downloadApp.ctaButtonText')}
          </S.Button>
        </S.Container>
      )
    }

    return (
      <S.Container>
        <S.SvgImage svg={paymentSuccessIcon} width={48} />
        <S.Title>
          {t(
            hasPurchasedUpsell
              ? 'downloadApp.titleUpsell'
              : 'downloadApp.titleSetUp',
          )}
        </S.Title>

        {hasPurchasedUpsell && (
          <S.Description>{t('downloadApp.subtitleUpsell')}</S.Description>
        )}

        <S.Subtitle>{t(SUBTITLE_KEY_BY_APP[appName])}</S.Subtitle>

        <S.Button type="button" onClick={handleClick}>
          {t('downloadApp.ctaGoToCourse')}
        </S.Button>
      </S.Container>
    )
  }

  return isLoadingUserStatus ? (
    <Spinner />
  ) : (
    <S.Wrapper
      $isScrollable={isUserHasAccount}
      $isCompactLayout={hasProgressBar}
    >
      {renderContent()}

      {!isUserHasAccount ? (
        <S.SaleImage src={saleImageSrc} />
      ) : (
        <S.Disclaimer $isCompactLayout={hasProgressBar}>
          <S.DisclaimerText>
            <Trans i18nKey="downloadApp.disclaimerText" />{' '}
            <a href={`mailto: ${supportLink}`}>{supportMail}</a>
          </S.DisclaimerText>
        </S.Disclaimer>
      )}
    </S.Wrapper>
  )
}
